<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import lumeratecConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TasksAPI from 'dashboard/api/lumerai/tasks';
import { emitter } from 'shared/helpers/mitt';
import { useAccount } from 'dashboard/composables/useAccount';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox, isAWhatsAppChannel } = useInbox();
const { currentAccount } = useAccount();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);
const showShopifyCart = computed(
  () =>
    isAWhatsAppChannel.value &&
    currentAccount.value?.shopify_catalog_available === true
);
const catalogCartCount = ref(0);

const onCatalogCartUpdated = ({ conversationId, itemCount }) => {
  if (Number(conversationId) === Number(props.chat.id)) {
    catalogCartCount.value = Number(itemCount) || 0;
  }
};

const openCatalogCart = () => {
  emitter.emit('shopify-cart:open', { conversationId: props.chat.id });
};

watch(
  [() => props.chat.id, showShopifyCart],
  async ([conversationId, enabled], _previous, onCleanup) => {
    let active = true;
    onCleanup(() => {
      active = false;
    });
    catalogCartCount.value = 0;
    if (!conversationId || !enabled) return;

    try {
      const { data } = await TasksAPI.catalogCartShow({ conversationId });
      if (active) catalogCartCount.value = Number(data.item_count) || 0;
    } catch (_error) {
      // The badge is best-effort; opening the cart shows an actionable error.
    }
  },
  { immediate: true }
);

onMounted(() => emitter.on('shopify-cart:updated', onCatalogCartUpdated));
onBeforeUnmount(() =>
  emitter.off('shopify-cart:updated', onCatalogCartUpdated)
);

const chatMetadata = computed(() => props.chat.meta);

const latestContactMessage = computed(() => {
  if (!props.chat.messages) return null;
  const incomingMessages = props.chat.messages.filter(
    m => m.message_type === 0
  );
  return incomingMessages.length
    ? incomingMessages[incomingMessages.length - 1]
    : null;
});

const contactSentiment = computed(() => {
  const msg = latestContactMessage.value;
  return (
    msg?.sentiment?.value ||
    props.chat?.custom_attributes?.sentiment ||
    'neutral'
  );
});

const emojiStyle = computed(() => {
  return store.getters.getUISettings?.ai_emoji_style || 'static';
});

// Activity messages arrive through ActionCable without reloading the
// conversation header payload. Keep the header in sync immediately after an
// appointment is created by reading its appointment link as a fallback.
const linkedCalendarAppointment = computed(() => {
  if (props.chat.calendar_appointment) return props.chat.calendar_appointment;

  const activity = [...(props.chat.messages || [])]
    .reverse()
    .find(message =>
      /\/calendar\?appointment_id=\d+/.test(message.content || '')
    );
  const match = activity?.content?.match(/appointment_id=(\d+)/);
  return match
    ? { id: match[1], title: t('CONVERSATION.CALENDAR_APPOINTMENT') }
    : null;
});

const calendarAppointmentLabel = computed(() => {
  const appointment = linkedCalendarAppointment.value;
  if (!appointment?.starts_at) return t('CONVERSATION.CALENDAR');

  const date = new Date(appointment.starts_at);
  if (Number.isNaN(date.getTime())) return t('CONVERSATION.CALENDAR');

  const dateLocale = locale.value.replace('_', '-');

  const dateLabel = new Intl.DateTimeFormat(dateLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: appointment.timezone || 'America/Sao_Paulo',
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat(dateLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: appointment.timezone || 'America/Sao_Paulo',
  }).format(date);

  return t('CONVERSATION.CALENDAR_APPOINTMENT_LABEL', {
    date: dateLabel,
    time: timeLabel,
  });
});

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// Keep the active-conversation heading consistent with the account's contact
// presentation configuration (for example, "Customer ID - Full name").
const currentContactDisplayName = computed(() => {
  const contact = currentContact.value || {};
  return contact.displayLabel || contact.display_label || contact.name;
});

const isSnoozed = computed(
  () => currentChat.value.status === lumeratecConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <span
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ currentContactDisplayName }}
          </span>
          <span
            v-if="contactSentiment"
            class="ml-1 text-sm"
            :class="{ 'animated-emoji': emojiStyle === 'animated' }"
          >
            {{
              contactSentiment === 'positive'
                ? $t('COMMON.SENTIMENT_POSITIVE')
                : contactSentiment === 'negative'
                  ? $t('COMMON.SENTIMENT_NEGATIVE')
                  : $t('COMMON.SENTIMENT_NEUTRAL')
            }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-1 overflow-hidden text-xs conversation--header--actions text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          <button
            type="button"
            class="truncate text-label-small text-n-slate-11 hover:text-n-slate-12 !p-0 cucursor-pointer"
            @click="copyConversationId"
          >
            {{ $t('COMMON.HASH_PREFIX') }}{{ chat.id }}
          </button>
          <span v-if="hasMultipleInboxes">
            {{ $t('COMMON.BULLET_SEPARATOR') }}
          </span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />

          <template v-if="chat.ticket">
            <span>{{ $t('COMMON.BULLET_SEPARATOR') }}</span>
            <router-link
              :to="{
                name: 'tickets_dashboard_index',
                params: { accountId: accountId },
                query: { ticket_id: chat.ticket.id },
              }"
              class="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium px-1 rounded hover:bg-blue-50 transition-colors"
            >
              <span>{{ $t('COMMON.TICKET_NUMBER') }}{{ chat.ticket.id }}</span>
            </router-link>
          </template>

          <template v-if="chat.sales_opportunity">
            <span>{{ $t('COMMON.BULLET_SEPARATOR') }}</span>
            <router-link
              :to="{
                name: 'sales_pipeline_index',
                params: { accountId: accountId },
                query: { opportunity_id: chat.sales_opportunity.id },
              }"
              class="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium px-1 rounded hover:bg-blue-50 transition-colors"
            >
              <span>{{ `Pipeline #${chat.sales_opportunity.id}` }}</span>
            </router-link>
          </template>

          <template v-if="linkedCalendarAppointment">
            <span>{{ $t('COMMON.BULLET_SEPARATOR') }}</span>
            <router-link
              :to="{
                name: 'calendar_index',
                params: { accountId: accountId },
                query: { appointment_id: linkedCalendarAppointment.id },
              }"
              class="flex items-center gap-1 whitespace-nowrap rounded px-1 font-medium text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700"
            >
              <span>{{ calendarAppointmentLabel }}</span>
            </router-link>
          </template>

          <span v-if="isSnoozed">{{ $t('COMMON.BULLET_SEPARATOR') }}</span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <button
        v-if="showShopifyCart"
        type="button"
        class="relative flex items-center justify-center rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-label="
          $t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CATALOG_CART')
        "
        :title="$t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CATALOG_CART')"
        @click="openCatalogCart"
      >
        <Icon icon="i-fluent-cart-24-regular" />
        <span
          v-if="catalogCartCount > 0"
          class="absolute -right-1 -top-1 min-w-4 rounded-full bg-n-brand text-center text-[10px] leading-4 text-white"
        >
          {{
            catalogCartCount > 99
              ? $t(
                  'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.CATALOG_CART_BADGE_MORE'
                )
              : catalogCartCount
          }}
        </span>
      </button>
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>

<style scoped>
@keyframes ai-pulse-animation {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
  }
}
.animated-emoji {
  display: inline-block;
  animation: ai-pulse-animation 1.5s infinite ease-in-out;
}
</style>
